import { Check, CheckCircle2, Clock, Hourglass } from 'lucide-react';
import { Appointment } from '../services/patientApi';
import { getConfirmationState } from '../utils/appointmentRequests';

interface ConfirmationBadgeProps {
  appt: Appointment;
  /**
   *  - "soft": rounded-md, semibold (table cell style).
   *  - "pill": rounded-full uppercase tracking-wider (header style).
   */
  variant?: 'soft' | 'pill';
}

const SOFT_BASE =
  'inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md';
const PILL_BASE =
  'inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full';

export const ConfirmationBadge = ({ appt, variant = 'soft' }: ConfirmationBadgeProps) => {
  const state = getConfirmationState(appt);

  // Cancelled appointments: confirmation isn't applicable. Show an em-dash placeholder.
  if (state === 'not-applicable') {
    return <div className="flex justify-center text-secondary-400">—</div>;
  }

  if (state === 'confirmed') {
    const Icon = variant === 'pill' ? Check : CheckCircle2;
    const base = variant === 'pill' ? PILL_BASE : SOFT_BASE;
    const iconProps = variant === 'pill' ? { strokeWidth: 3 } : {};
    return (
      <span className={`${base} bg-green-50 text-green-700`}>
        <Icon size={12} {...iconProps} />
        Confirmed
      </span>
    );
  }

  // Patient already submitted a confirm request — office is reviewing it.
  // Blue tone (vs amber for plain "Pending") signals "in flight with the
  // office" rather than "patient hasn't acted yet".
  if (state === 'pending-request') {
    return (
      <span className={`${SOFT_BASE} bg-blue-50 text-blue-700`}>
        <Hourglass size={12} />
        Pending request
      </span>
    );
  }

  // 'pending' — nothing has happened yet.
  return (
    <span className={`${SOFT_BASE} bg-amber-50 text-amber-700`}>
      <Clock size={12} />
      Pending
    </span>
  );
};
